<template>
  <section class="featured section-wrap">
    <div class="title-row">
      <h2 class="featured-title">
        FEATURED CATEGORIES
      </h2>
      <router-link
        class="btn btn-solid featured-all-btn featured-all-btn--desktop"
        to="/goods"
      >
        All Products
      </router-link>
    </div>
    <div
      class="category-row-wrapper"
      :class="{ 'is-static': state.categoryStatic }"
    >
      <div
        ref="catRow"
        class="category-row"
      >
        <router-link
          v-for="c in categoryLoop"
          :key="c._uid"
          :to="{ path: '/goods', query: { categoryName: c.categoryName } }"
          class="category-card"
        >
          <span class="category-label">
            {{ c.categoryName }}
          </span>
          
          <img
            :src="c.goodsImage"
            :alt="c.categoryName"
            :loading="state.imgLoadingMode"
          />
          
          <span class="category-btn">
            View Collection
          </span>
        </router-link>
      </div>
    </div>
    <router-link
      class="btn btn-solid featured-all-btn featured-all-btn--mobile"
      to="/goods"
    >
      All Products
    </router-link>
  </section>
</template>

<script>
import { setupCategoryMarquee } from '../../finds-marquee'

export default {
  name: 'HomeFeaturedCategories',
  props: {
    goodsList: {
      type: Object,
      default: () => ({})
    },
    lockMobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      state: {
        categoryStatic: false,
        // PC 端跑马灯持续位移场景下，使用 eager 避免图片延迟绘制导致“有元素无图”。
        imgLoadingMode: this.lockMobile ? 'lazy' : 'eager'
      },
      _onResize: null,
      _resizeRaf: 0,
      _lastWrapWidth: 0,
      _catDragCleanup: null,
      _catLoopDistance: 0,
      _inertiaRaf: null,
      dragState: {
        isActive: false,
        moved: false,
        startX: 0,
        startTime: 0,
        lastX: 0,
        lastTime: 0,
        velocity: 0
      }
    }
  },
  computed: {
    categoryLoop() {
      const base = (this.goodsList?.['Categories'] || []).map((c, i) => ({
        ...c,
        _uid: c.key + '-' + i
      }))
      if (this.lockMobile) {
        return base
      }
      // 数据量少时多套复制确保填满屏幕，避免滚动出现空白
      const minCopies = base.length < 4 ? 8 : 4
      const copies = Array(minCopies).fill(base).flat()
      return copies.map((c, idx) => ({
        ...c,
        _uid: c._uid + '-r' + idx
      }))
    },
    catPeriodLen() {
      return this.goodsList?.['Categories']?.length || 0
    }
  },
  watch: {
    catPeriodLen() {
      this.$nextTick(() => {
        this.initCategoryMarquee()
      })
    },
    lockMobile() {
      this.state.imgLoadingMode = this.lockMobile ? 'lazy' : 'eager'
      this.$nextTick(() => {
        this.initCategoryMarquee()
      })
    }
  },
  mounted() {
    if (!this.lockMobile) {
      this._onResize = () => this.queueCategoryMarqueeUpdate()
      window.addEventListener('resize', this._onResize)
    }
    this.$nextTick(() => {
      this.initCategoryMarquee()
    })
  },
  beforeDestroy() {
    if (this._onResize) window.removeEventListener('resize', this._onResize)
    this._onResize = null
    if (this._resizeRaf) {
      cancelAnimationFrame(this._resizeRaf)
      this._resizeRaf = 0
    }
    this.teardownCategoryMarquee()
    this.teardownCategoryDrag()
    this.stopInertia()
  },
  methods: {
    queueCategoryMarqueeUpdate() {
      if (this._resizeRaf) {
        cancelAnimationFrame(this._resizeRaf)
      }
      this._resizeRaf = requestAnimationFrame(() => {
        this._resizeRaf = 0
        const row = this.$refs.catRow
        const wrapWidth = row?.parentElement?.clientWidth || 0
        if (!wrapWidth) return
        // iOS 地址栏伸缩会频繁触发 resize，宽度无变化时跳过重建。
        if (Math.abs(wrapWidth - this._lastWrapWidth) < 2) return
        this.initCategoryMarquee()
      })
    },
    teardownCategoryMarquee() {
      const row = this.$refs.catRow
      if (row && row._marquee) {
        row._marquee.cancel()
        row._marquee = null
      }
    },
    initCategoryMarquee() {
      this.teardownCategoryMarquee()
      this.teardownCategoryDrag()
      this._catLoopDistance = 0
      const row = this.$refs.catRow
      if (!row) return

      const parentWidth = row.parentElement?.clientWidth || 0
      this._lastWrapWidth = parentWidth
      const children = Array.from(row.children)
      const style = getComputedStyle(row)
      const gapPx = Number.parseFloat(style.columnGap || style.gap || '0') || 0
      const contentWidth = children.reduce((sum, el, idx) => {
        const next = sum + el.offsetWidth
        return idx < children.length - 1 ? next + gapPx : next
      }, 0)

      // 内容不足一屏时保持静态，避免无意义滚动。
      const shouldStatic = contentWidth <= parentWidth + 2
      this.state.categoryStatic = shouldStatic
      if (shouldStatic) return

      this._catLoopDistance = setupCategoryMarquee(row, this.catPeriodLen)
      this.bindCategoryDrag()
    },
    teardownCategoryDrag() {
      if (this._catDragCleanup) {
        this._catDragCleanup()
        this._catDragCleanup = null
      }
    },
    getMarqueeMeta(row) {
      const marquee = row && row._marquee
      if (!marquee || !marquee.effect || typeof marquee.effect.getKeyframes !== 'function') return null
      const keyframes = marquee.effect.getKeyframes()
      const targetFrame = keyframes[keyframes.length - 1] || {}
      const transformText = String(targetFrame.transform || '')
      const match = transformText.match(/translateX\((-?[\d.]+)px\)/)
      if (!match) return null
      const loopDistance = Math.abs(Number(match[1]))
      const timing = marquee.effect.getComputedTiming()
      const duration = Number(timing.duration) || 0
      if (!loopDistance || !duration) return null
      return {
        loopDistance,
        duration,
        msPerPx: duration / loopDistance
      }
    },
    normalizeMarqueeTime(row, time) {
      const duration = Number(row?._marquee?.effect?.getTiming?.()?.duration) || 0
      if (!duration) return Number(time) || 0
      // 将时间归一化到单个循环内，避免快速拖拽时出现空白帧。
      const normalized = (Number(time) || 0) % duration
      return normalized < 0 ? normalized + duration : normalized
    },
    stopInertia() {
      if (this._inertiaRaf) {
        cancelAnimationFrame(this._inertiaRaf)
        this._inertiaRaf = null
      }
    },
    startInertia(initialVelocity) {
      const row = this.$refs.catRow
      if (!row || !row._marquee) return
      const meta = this.getMarqueeMeta(row)
      if (!meta) return

      let velocity = initialVelocity * 1200
      const friction = 0.99
      const minVelocity = 0.2

      const step = () => {
        if (Math.abs(velocity) < minVelocity) {
          this._inertiaRaf = null
          if (row._marquee) row._marquee.play()
          return
        }
        if (!row || !row._marquee) {
          this._inertiaRaf = null
          return
        }
        const currentTime = Number(row._marquee.currentTime) || 0
        const pxPerMs = 1 / meta.msPerPx
        const deltaTime = -velocity * pxPerMs * 16
        row._marquee.currentTime = this.normalizeMarqueeTime(row, currentTime + deltaTime)
        velocity *= friction
        this._inertiaRaf = requestAnimationFrame(step)
      }
      this._inertiaRaf = requestAnimationFrame(step)
    },
    bindCategoryDrag() {
      this.teardownCategoryDrag()
      this.stopInertia()
      const row = this.$refs.catRow
      const wrap = row?.parentElement
      if (!row || !wrap || !this._catLoopDistance) return

      const onPointerDown = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return
        if (!row._marquee) return
        this.stopInertia()
        this.dragState.isActive = true
        this.dragState.moved = false
        this.dragState.startX = event.clientX
        this.dragState.lastX = event.clientX
        this.dragState.lastTime = Date.now()
        this.dragState.velocity = 0
        this.dragState.startTime = Number(row._marquee.currentTime || 0)
        row._marquee.pause()
        wrap.classList.add('is-dragging')
      }

      const onPointerMove = (event) => {
        if (!this.dragState.isActive || !row._marquee || !this._catLoopDistance) return
        const deltaX = event.clientX - this.dragState.startX
        if (!this.dragState.moved && Math.abs(deltaX) > 4) {
          this.dragState.moved = true
        }

        // 计算速度用于惯性
        const now = Date.now()
        const stepX = event.clientX - this.dragState.lastX
        const dt = now - this.dragState.lastTime
        this.dragState.lastX = event.clientX
        this.dragState.lastTime = now
        if (dt > 0) {
          this.dragState.velocity = stepX / dt
        }

        const timing = row._marquee.effect?.getTiming?.() || {}
        const duration = Number(timing.duration) || 0
        if (!duration) return
        const deltaTime = (-deltaX / this._catLoopDistance) * duration
        row._marquee.currentTime = this.normalizeMarqueeTime(row, this.dragState.startTime + deltaTime)
        if (this.dragState.moved) {
          event.preventDefault()
        }
      }

      const onPointerUp = (event) => {
        if (!this.dragState.isActive) return
        this.dragState.isActive = false
        wrap.classList.remove('is-dragging')
        const velocity = this.dragState.velocity
        if (Math.abs(velocity) > 0.1) {
          this.startInertia(velocity)
        } else {
          if (row._marquee) row._marquee.play()
        }
      }

      const onClickCapture = (event) => {
        if (!this.dragState.moved) return
        event.preventDefault()
        event.stopPropagation()
        this.dragState.moved = false
      }

      const onNativeDragStart = (event) => {
        event.preventDefault()
      }

      const onSelectStart = (event) => {
        event.preventDefault()
      }

      wrap.addEventListener('pointerdown', onPointerDown)
      wrap.addEventListener('pointermove', onPointerMove)
      wrap.addEventListener('pointerup', onPointerUp)
      wrap.addEventListener('pointercancel', onPointerUp)
      wrap.addEventListener('pointerleave', onPointerUp)
      row.addEventListener('dragstart', onNativeDragStart)
      row.addEventListener('selectstart', onSelectStart)
      row.addEventListener('click', onClickCapture, true)

      this._catDragCleanup = () => {
        wrap.classList.remove('is-dragging')
        wrap.removeEventListener('pointerdown', onPointerDown)
        wrap.removeEventListener('pointermove', onPointerMove)
        wrap.removeEventListener('pointerup', onPointerUp)
        wrap.removeEventListener('pointercancel', onPointerUp)
        wrap.removeEventListener('pointerleave', onPointerUp)
        row.removeEventListener('dragstart', onNativeDragStart)
        row.removeEventListener('selectstart', onSelectStart)
        row.removeEventListener('click', onClickCapture, true)
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../styles/type1-style.scss';
@import '../../styles/find-base.css';

/* 给 wrapper 添加底部 padding 容纳溢出的按钮 */
.category-row-wrapper {
  padding: 50px 0;
  overflow: visible;
}

/* 大屏尺寸 342*317 */
.category-card {
  display: block;
  width: 342px;
  -webkit-box-flex: 0;
  -webkit-flex: 0 0 342px;
  flex: 0 0 342px;
  min-width: 342px;
  min-height: 317px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background: hsl(from #373737 h s l / 0.5);
  padding: 0.7rem;
  color: var(--text);
  text-decoration: none;
  -webkit-transition: transform 0.18s ease, border-color 0.18s ease;
  transition: transform 0.18s ease, border-color 0.18s ease;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -webkit-flex-direction: column;
  flex-direction: column;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  -webkit-box-pack: justify;
  -webkit-justify-content: space-between;
  justify-content: space-between;
  gap: 0.55rem;
  position: relative;
}

.category-card + .category-card {
  margin-left: 0.8rem;
}

.category-card img {
  width: 100%;
  height: auto;
  object-fit: contain;
}

/* 分类跑马灯内卡片始终可见，避免在部分浏览器中被 zoom-reveal 保持为透明。 */
.category-row .category-card.zoom-reveal {
  opacity: 1 !important;
  -webkit-transform: none !important;
  transform: none !important;
  filter: none !important;
}

.category-btn {
  position: absolute;
  bottom: -50px;
  left: 50%;
  -webkit-transform: translateX(-50%);
  transform: translateX(-50%);
  padding: 8px 20px;
  background: var(--primary-color);
  color: var(--background-color);
  border-radius: 20px;
  font-size: 1rem;
  font-weight: 600;
  opacity: 0;
  visibility: hidden;
  -webkit-transition: opacity 0.2s ease, visibility 0.2s ease;
  transition: opacity 0.2s ease, visibility 0.2s ease;
}

.category-card:hover .category-btn,
.category-btn:hover {
  opacity: 1;
  visibility: visible;
}

.category-card:hover {
  -webkit-transform: translateY(-1px);
  transform: translateY(-1px);
  border-color: var(--accent);
}

/* 小屏尺寸 250*250 */
@media (max-width: 980px) {
  .category-card {
    width: 250px;
    -webkit-box-flex: 0;
    -webkit-flex: 0 0 250px;
    flex: 0 0 250px;
    min-width: 250px;
    min-height: 250px;
  }

.category-btn {
display: none;
}

  .category-card img {
    height: auto;
  }
}
</style>